import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'

export interface SequenceButton {
  text: string
  url: string
  /** 'reply' = postback (reabre a janela de 24h). Ausente = web_url. */
  type?: 'reply'
}

export interface SequenceStep {
  delayDays: number
  message: string
  imageUrl?: string
  audioUrl?: string
  buttons?: SequenceButton[]
  /** Nome do template aprovado (máscara) usado p/ furar a janela de 24h. */
  templateName?: string
  templateHasVar?: boolean
}

export interface SequenceListItem {
  id: string
  name: string
  steps: SequenceStep[]
  platform: 'ig' | 'fb'
  accountId: string
  accountName: string
  isActive?: boolean
}

export function useSequences() {
  return useQuery<SequenceListItem[]>({
    queryKey: ['sequences'],
    queryFn: () => api.get('/sequences'),
  })
}

export function useCreateSequence() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      accountId: string
      platform: string
      name: string
      steps: { delayDays: number; message: string; buttons?: SequenceButton[]; templateName?: string }[]
    }) => api.post('/sequences', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sequences'] }),
  })
}

export function useDeleteSequence() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ sequenceId, accountId, platform }: { sequenceId: string; accountId: string; platform: string }) =>
      api.delete(`/sequences/${sequenceId}`, { accountId, platform }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sequences'] }),
  })
}

export function useUpdateSequence() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ sequenceId, accountId, platform, name, steps }: {
      sequenceId: string; accountId: string; platform: string; name: string;
      steps: SequenceStep[]
    }) => api.put(`/sequences/${sequenceId}`, { accountId, platform, name, steps }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sequences'] }),
  })
}

export function useEnrollSequence() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ sequenceId, accountId, platform }: { sequenceId: string; accountId: string; platform: string }) =>
      api.post<{ ok: boolean; enrolled: number; total: number }>(`/sequences/${sequenceId}/enroll`, { accountId, platform }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sequences'] }),
  })
}

// ─── Estatísticas de clique nas etapas ───
// Mesmo tipo de leitura do card de captura: por etapa, por dia e por página.
// Filtro de data opcional (inclusivo nos dois extremos).

export interface SequenceClickStats {
  total: number
  totalPaginas: number
  from: string | null
  to: string | null
  porEtapa: { etapa: number; cliques: number; envios?: number; ctr?: number | null }[]
  porDia: { dia: string; cliques: number }[]
  porPagina: { pagina: string; cliques: number }[]
}

export function useSequenceClickStats(from?: string, to?: string, platform?: 'ig' | 'fb') {
  const params = new URLSearchParams()
  if (from) params.set('from', from)
  if (to) params.set('to', to)
  if (platform) params.set('platform', platform)
  const qs = params.toString()

  return useQuery<SequenceClickStats>({
    queryKey: ['sequence-click-stats', from ?? '', to ?? '', platform ?? ''],
    queryFn: () => api.get(`/sequences/click-stats${qs ? `?${qs}` : ''}`),
    refetchInterval: 60_000, // igual ao painel de cards: atualiza a cada 1 min
  })
}


// Liga/desliga a sequencia. Pausar nao apaga nada — os leads ficam parados
// onde estao e retomam do mesmo ponto quando reativar.
export function useToggleSequence() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.post(`/sequences/${id}/toggle`, { isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sequences'] }),
  })
}


// Liga/desliga TODAS as sequencias de uma vez. Com 150+ paginas o toggle
// individual nao da conta.
export function useBulkToggleSequences() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ isActive, platform }: { isActive: boolean; platform?: 'ig' | 'fb' }) =>
      api.post('/sequences/bulk-toggle', { isActive, platform }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sequences'] }),
  })
}
